import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import crmService from '../../../services/crmService';
import clientRequirementsService from '../../../services/clientRequirementsService';

const SendToClientModal = ({ isOpen, onClose, lead, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [formData, setFormData] = useState({
    requirement_id: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && lead) {
      loadRequirements();
      setFormData({
        requirement_id: '',
        notes: ''
      });
      setError(null);
    }
  }, [isOpen, lead]);

  const loadRequirements = async () => {
    try {
      setLoadingRequirements(true);
      // Get all open requirements
      const result = await clientRequirementsService.getRequirements({ 
        status: 'open' 
      });
      if (result?.success) {
        setRequirements(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load requirements:', err);
    } finally {
      setLoadingRequirements(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.requirement_id) {
      setError('Please select a client requirement');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await crmService.sendLeadToClient(lead.id, {
        requirement_id: formData.requirement_id,
        notes: formData.notes
      });

      if (result?.success) {
        onSent?.(result.data);
      } else {
        setError(result?.error || 'Failed to send lead to client');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Send to client error:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedRequirement = requirements.find(r => r.id === formData.requirement_id);

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Send" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">Send Lead to Client</h3>
              <p className="text-sm text-muted-foreground">{lead?.name}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Lead Info */}
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h4 className="text-sm font-semibold text-foreground mb-2">Lead Information</h4>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Icon name="User" size={14} className="text-muted-foreground" />
              <span className="text-foreground font-medium">{lead?.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Phone" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{lead?.phone}</span>
            </div>
            {lead?.email && (
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={14} className="text-muted-foreground" />
                <span className="text-foreground">{lead?.email}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Icon name="Briefcase" size={14} className="text-muted-foreground" />
              <span className="text-foreground">
                {lead?.experience_years || 0} years • {lead?.skill_level || 'entry'} level
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Client Requirement *
            </label>
            {loadingRequirements ? (
              <div className="p-4 text-center">
                <Icon name="Loader" size={24} className="animate-spin text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">Loading requirements...</p>
              </div>
            ) : requirements.length === 0 ? (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  No open client requirements found. Please create a requirement first.
                </p>
              </div>
            ) : (
              <Select
                value={formData.requirement_id}
                onValueChange={(value) => handleInputChange('requirement_id', value)}
                placeholder="Select a client requirement..."
                required
                options={[
                  { value: '', label: 'Select a client requirement...' },
                  ...requirements.map(req => ({
                    value: req.id,
                    label: `${req.position_title || req.title} - ${req.clients?.company_name || 'Client'} (Qty: ${req.quantity_needed || 1})`
                  }))
                ]}
              />
            )}
          </div>

          {/* Selected Requirement Details */}
          {selectedRequirement && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h4 className="text-sm font-semibold text-foreground mb-2">Requirement Details</h4>
              <div className="space-y-1 text-sm">
                <p><span className="font-medium">Position:</span> {selectedRequirement.position_title || selectedRequirement.title}</p>
                {selectedRequirement.department && (
                  <p><span className="font-medium">Department:</span> {selectedRequirement.department}</p>
                )}
                <p><span className="font-medium">Quantity Needed:</span> {selectedRequirement.quantity_needed || 1}</p>
                {selectedRequirement.experience_level && (
                  <p><span className="font-medium">Experience:</span> {selectedRequirement.experience_level}</p>
                )}
                {selectedRequirement.clients?.company_name && (
                  <p><span className="font-medium">Client:</span> {selectedRequirement.clients.company_name}</p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              rows="3"
              placeholder="Add any notes about this submission..."
            />
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-medium mb-1">What happens when you send:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Lead status will change to "Sent to Client"</li>
                  <li>A submission record will be created</li>
                  <li>You can track client response in Client Requirements</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !formData.requirement_id || requirements.length === 0}>
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Icon name="Send" size={16} className="mr-2" />
                  Send to Client
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendToClientModal;

